/* eslint-disable @typescript-eslint/no-explicit-any */
import { Commit } from 'vuex';
import {
  MantecaWithdrawal,
  MantecaWithdrawalListItem,
  MantecaWithdrawalCreatePayload,
  MantecaWithdrawalsListParams,
  MantecaWithdrawalsState,
  MantecaWithdrawalBalanceRequest,
  MantecaWithdrawalBalanceResponse,
  MantecaWithdrawalBatchRequest
} from '../interfaces/mantecaWithdrawals';
import { httpCommpix } from '@/util/HTTPCommpix';
import types from '../types/mantecaWithdrawals';

export type Pagination = {
  count: number;
  page: number;
  per_page: number;
};

const BASE_PATH = '/dashboard-central/manteca/withdrawals';

const state: MantecaWithdrawalsState = {
  data: [],
  page: 1,
  pageSize: 10,
  totalRecords: 0,
  loading: false,
};

const mutations = {
  // eslint-disable-next-line @typescript-eslint/explicit-module-boundary-types
  [types.mutations.SET_WITHDRAWALS](
    state: MantecaWithdrawalsState,
    payload: {
      data: MantecaWithdrawalListItem[];
      page: number;
      pageSize: number;
      totalRecords: number;
    }
  ) {
    state.data = payload.data;
    state.page = payload.page;
    state.pageSize = payload.pageSize;
    state.totalRecords = payload.totalRecords;
  },
  // eslint-disable-next-line @typescript-eslint/explicit-module-boundary-types
  [types.mutations.SET_LOADING](state: MantecaWithdrawalsState, loading: boolean) {
    state.loading = loading;
  },
};

const actions = {
  [types.actions.WITHDRAWAL_LIST]: (
    { commit, }: { commit: Commit },
    params: MantecaWithdrawalsListParams = {}
  ): Promise<any> => {
    return new Promise((resolve, reject) => {
      commit(types.mutations.SET_LOADING, true);
      const {
        page = 1,
        pageSize = 10,
        status,
        from,
        to,
        companyId,
        commerceId,
        branchId,
      } = params;
      const body: Record<string, any> = { page, pageSize, };
      if (status) body.status = status;
      if (from) body.from = from;
      if (to) body.to = to;
      // Optional, independent tenant-scope filters (hierarchical narrowing:
      // company → company+commerce → company+commerce+branch). Sent only when set.
      if (companyId) body.companyId = companyId;
      if (commerceId) body.commerceId = commerceId;
      if (branchId) body.branchId = branchId;

      httpCommpix
        .post(`${BASE_PATH}/list`, body)
        .then((response) => {
          const {
            data,
            page: respPage,
            pageSize: respPageSize,
            total,
          } = response.data;
          response.data.pagination = {
            per_page: respPageSize,
            count: total,
            page: respPage,
          };
          commit(types.mutations.SET_WITHDRAWALS, {
            data,
            page: respPage,
            pageSize: respPageSize,
            totalRecords: total,
          });
          resolve(response);
        })
        .catch((error) => {
          commit(types.mutations.SET_WITHDRAWALS, {
            data: [],
            page: 1,
            pageSize: 10,
            totalRecords: 0,
          });
          reject(error);
        })
        .finally(() => {
          commit(types.mutations.SET_LOADING, false);
        });
    });
  },
  [types.actions.WITHDRAWAL_CREATE]: (
    { commit, }: { commit: Commit },
    payload: MantecaWithdrawalCreatePayload
  ): Promise<MantecaWithdrawal> => {
    return new Promise((resolve, reject) => {
      commit(types.mutations.SET_LOADING, true);
      httpCommpix
        .post(BASE_PATH, payload)
        .then((response) => {
          resolve(response.data);
        })
        .catch((error) => {
          reject(error);
        })
        .finally(() => {
          commit(types.mutations.SET_LOADING, false);
        });
    });
  },
  [types.actions.WITHDRAWAL_GET_BY_ID]: (
    { commit, }: { commit: Commit },
    id: string
  ): Promise<MantecaWithdrawal> => {
    return new Promise((resolve, reject) => {
      commit(types.mutations.SET_LOADING, true);
      httpCommpix
        .get(`${BASE_PATH}/${id}`)
        .then((response) => {
          resolve(response.data);
        })
        .catch((error) => {
          reject(error);
        })
        .finally(() => {
          commit(types.mutations.SET_LOADING, false);
        });
    });
  },
  // Live, cache-bypassed available balance pre-flight check. Loading is tracked
  // locally by the modal (not via SET_LOADING) to avoid the global progress bar
  // flashing on every tenant selection change.
  [types.actions.WITHDRAWAL_BALANCE]: (
    _ctx: { commit: Commit },
    payload: MantecaWithdrawalBalanceRequest
  ): Promise<MantecaWithdrawalBalanceResponse> => {
    return new Promise((resolve, reject) => {
      httpCommpix
        .post(`${BASE_PATH}/balance`, payload)
        .then((response) => {
          resolve(response.data);
        })
        .catch((error) => {
          reject(error);
        });
    });
  },
  // One-shot sweep: withdraw the full balance of every Manteca merchant. The
  // endpoint is long-running (it processes every merchant synchronously) and
  // returns 204 No Content — there is no body to parse. A high timeout is set
  // so the client does not abort mid-sweep. The modal invokes this fire-and-forget;
  // no SET_LOADING toggle here so the global progress bar does not stay pinned for
  // the whole sweep.
  [types.actions.WITHDRAWAL_BATCH]: (
    _ctx: { commit: Commit },
    payload: MantecaWithdrawalBatchRequest
  ): Promise<void> => {
    return new Promise((resolve, reject) => {
      httpCommpix
        .post(`${BASE_PATH}/batch`, payload, { timeout: 300000, })
        .then(() => {
          resolve();
        })
        .catch((error) => {
          reject(error);
        });
    });
  },
};

const getters = {
  [types.getters.GET_WITHDRAWALS]: (
    state: MantecaWithdrawalsState
  ): { data: MantecaWithdrawalListItem[]; pagination: Pagination } => ({
    data: state.data,
    pagination: {
      count: state.totalRecords,
      page: state.page,
      per_page: state.pageSize,
    },
  }),
  [types.getters.GET_WITHDRAWALS_PAGINATION]: (state: MantecaWithdrawalsState): Pagination => ({
    count: state.totalRecords,
    page: state.page,
    per_page: state.pageSize,
  }),
  [types.getters.GET_LOADING]: (state: MantecaWithdrawalsState): boolean => state.loading,
};

export default {
  namespaced: true,
  state,
  mutations,
  actions,
  getters,
};
